import type {
	SawEntryQueueEntry,
	SawEntryWorkOrder,
	WorkOrderSwitch,
} from "./repositories/sawEntryQueueRepository";

export interface LoadedPrompt extends WorkOrderSwitch {
	reason: string;
	question: string;
	confirm: string;
	dismiss: string;
	complete?: SawEntryWorkOrder;
	start?: SawEntryWorkOrder;
	// snackbar tail: " · Work order X completed · Work order Y started"
	done: string;
}

const prompt = (
	reason: string,
	question: string,
	confirm: string,
	complete?: SawEntryWorkOrder,
	start?: SawEntryWorkOrder,
): LoadedPrompt => ({
	reason,
	question,
	confirm,
	dismiss: complete ? "Not yet" : "Not now",
	complete,
	start,
	completeWorkOrderId: complete?.WorkOrderId,
	startWorkOrderId: start?.WorkOrderId,
	done:
		(complete ? ` · Work order ${complete.WorkOrderNumber} completed` : "") +
		(start ? ` · Work order ${start.WorkOrderNumber} started` : ""),
});

// Loaded on the top tag: the WO switch it implies, operator confirms. null = plain Loaded.
export const loadedPrompt = (
	entry: SawEntryQueueEntry,
	running: SawEntryWorkOrder | undefined,
	next: SawEntryWorkOrder | undefined,
): LoadedPrompt | null => {
	const y = entry.MatchesNext ? next : undefined;
	if (!running)
		return y
			? prompt(
					`No work order is running on the saw and tag ${entry.TagNo} is for the next one.`,
					`Start work order ${y.WorkOrderNumber}?`,
					"Start work order",
					undefined,
					y,
				)
			: null;
	const x = `Work order ${running.WorkOrderNumber}`;
	if (running.PiecesCut >= running.Quantity) {
		const reached = `${x} reached its quantity (${running.PiecesCut} / ${running.Quantity} pcs)`;
		return y
			? prompt(
					`${reached} and tag ${entry.TagNo} is for the next one.`,
					`Complete ${running.WorkOrderNumber} and start ${y.WorkOrderNumber}?`,
					"Complete and start next",
					running,
					y,
				)
			: prompt(
					`${reached}.`,
					`Complete work order ${running.WorkOrderNumber}?`,
					"Complete work order",
					running,
				);
	}
	return y && !entry.MatchesRunning
		? prompt(
				`${x} still needs ${running.Quantity - running.PiecesCut} pcs, but tag ${entry.TagNo} is for the next one.`,
				`End ${running.WorkOrderNumber} early and start ${y.WorkOrderNumber}?`,
				"End early and start next",
				running,
				y,
			)
		: null;
};
